import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IonRouterOutlet, Platform, AlertController } from '@ionic/angular';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { PaymentService } from './core/services/payment.service';
import { StorageService } from './core/services/storage.service';
import { BASE_URL_PATH, STONE_CREEK_MERCHANT_ID, STORAGE_CUSTOMER_DATA } from './core/constants';
import { Router, NavigationEnd } from '@angular/router';
import { merchantConfig } from '../environments/merchant-config';
import { filter, switchMap, tap, catchError } from 'rxjs/operators';
import { Customer } from './core/models/customer.model';
import { SwipeGestureService } from './core/services/swipe-gesture.service';
import { RouteService } from './core/services/route.service';
import { Location } from '@angular/common';
import { UtilService } from './core/services/util.service';
import { Platforms } from './core/enums/util.enum';
import { SquarePayService } from './core/services/square-pay-service.service';
import { Title } from '@angular/platform-browser';
import { environment } from '../environments/environment';
import { TextSizeService } from './core/services/text-size.service';
import { OneSignalService } from './core/services/one-signal.service';
import { MerchantService } from './core/services/merchant.service';
import { DeepLinkService } from './core/services/deep-link.service';
import { PaymentStore } from './core/stores/payment.store';
import { RestaurantStore } from './core/stores/restaurant.store';
import { OrderWithFlavorStore } from './core/stores/orderwithflavor.store';
import { from, of, Subject } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';

declare const codePush: any;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(IonRouterOutlet, { static: false }) routerOutlet: IonRouterOutlet;
  private destroyed$ = new Subject<void>();

  constructor(
    private platform: Platform,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    private router: Router,
    private paymentService: PaymentService,
    private storageService: StorageService,
    private swipeGestureService: SwipeGestureService,
    private routeService: RouteService,
    private location: Location,
    public utilService: UtilService,
    private squarePayService: SquarePayService,
    private titleService: Title,
    private textSizeService: TextSizeService, // Initializes text size from localStorage
    private oneSignalService: OneSignalService,
    private merchantService: MerchantService,
    private deepLinkService: DeepLinkService,
    private paymentStore: PaymentStore,
    private restaurantStore: RestaurantStore,
    private orderWithFlavorStore: OrderWithFlavorStore,
    private alertController: AlertController
  ) {
    this.initializeApp();
  }

  private initializeApp(): void {
    this.platform.ready().then(async () => {
      if (this.platform.is(Platforms.CORDOVA)) {
        this.initializeStatusBar();
        this.listenRouteChanges();
        this.handleDeepLink();
        this.splashScreen.hide();
        this.squarePayService.checkCanUseWalletPay();
        await this.initializeOneSignal();
      }
    });
  }

  private handleDeepLink(): void {
    console.log('handleDeepLink initialized');

    // ionic-plugin-deeplinks uses the IonicDeeplink global
    if ((window as any).IonicDeeplink) {
      console.log('IonicDeeplink plugin found');

      // Define route patterns for the plugin
      const routes = {
        '/mobile-app/:appId/gift-card': {},
        '/mobile-app/gift-card': {},
        '/giftcard': {}
      };

      (window as any).IonicDeeplink.route(
        routes,
        (match: any) => {
          console.log('Deep link match:', JSON.stringify(match));

          // Extract query parameters from the match object
          const merchantId = match.$args?.merchantId || match.merchantId;
          const gan = match.$args?.gan || match.gan;

          if (merchantId && gan) {
            console.log('Processing gift card deep link via service');
            // Store for fallback (in case component isn't ready)
            this.storageService.set('pendingGiftCard', { merchantId, gan });
            // Emit event for immediate handling
            this.deepLinkService.emitGiftCardEvent(merchantId, gan);
          }
        },
        (nomatch: any) => {
          console.log('No deep link match:', JSON.stringify(nomatch));

          // Try to manually extract from the URL in nomatch
          const url = nomatch.$link?.url || nomatch.url || '';
          console.log('Attempting manual parse of URL:', url);

          if (url && (url.includes('/mobile-app/') || url.includes('giftcard'))) {
            try {
              const urlObj = new URL(url);
              const merchantId = urlObj.searchParams.get('merchantId');
              const gan = urlObj.searchParams.get('gan');

              console.log('Manual parse - Gift card params:', { merchantId, gan });

              if (merchantId && gan) {
                console.log('Processing gift card deep link from manual parse via service');
                // Store for fallback (in case component isn't ready)
                this.storageService.set('pendingGiftCard', { merchantId, gan });
                // Emit event for immediate handling
                this.deepLinkService.emitGiftCardEvent(merchantId, gan);
              }
            } catch (error) {
              console.error('Error in manual URL parsing:', error);
            }
          }
        }
      );
    }

    // Fallback: Handle custom URL scheme (kyoo://)
    (window as any).handleOpenURL = (url: string) => {
      console.log('handleOpenURL called with:', url);
      if (url.includes('giftcard')) {
        try {
          // Parse custom URL scheme: kyoo://giftcard?merchantId=X&gan=Y
          const urlObj = new URL(url);
          const merchantId = urlObj.searchParams.get('merchantId');
          const gan = urlObj.searchParams.get('gan');

          console.log('Custom URL scheme params:', { merchantId, gan });

          if (merchantId && gan) {
            console.log('Processing gift card deep link from custom URL via service');
            // Store for fallback (in case component isn't ready)
            this.storageService.set('pendingGiftCard', { merchantId, gan });
            // Emit event for immediate handling
            this.deepLinkService.emitGiftCardEvent(merchantId, gan);
          }
        } catch (error) {
          console.error('Error parsing custom URL:', error);
        }
      }
    };
  }

  private async initializeOneSignal(): Promise<void> {
    try {
      const merchantId = merchantConfig.id;
      if (!merchantId) {
        console.log('OneSignal setup skipped: No merchant ID found');
        return;
      }

      this.merchantService.getMerchantMobileApp(merchantId).subscribe(
        (mobileApp) => {
          if (mobileApp && mobileApp.oneSignalAppId) {
            this.oneSignalService.setupPush(mobileApp.oneSignalAppId);
          } else {
            console.log('OneSignal setup skipped: No oneSignalAppId in merchant mobile app data');
          }
        },
        (error) => {
          console.error('Failed to fetch merchant mobile app data:', error);
        }
      );
    } catch (error) {
      console.error('Error initializing OneSignal:', error);
    }
  }

  private initializeStatusBar(): void {
    this.statusBar.show();
    if (this.platform.is(Platforms.ANDROID)) {
      this.statusBar.overlaysWebView(false);
    } else {
      this.statusBar.overlaysWebView(true);
    }
  }

  private listenRouteChanges(): void {
    if (this.platform.is(Platforms.IOS)) {
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.statusBar.styleDefault()
        }
      });
    }
  }

  ngOnInit() {
    this.titleService.setTitle(environment.appName);
    this.setDynamicFavicon();
    this.routeService.checkParams();
    if (this.platform.is(Platforms.CORDOVA)) {
      this.setMerchantIfNotExists();
      this.subscribeToDeepLinkEvents();
    }
    this.updateCustomerPlatform();
    this.listenAppResume();
  }

  private listenAppResume(): void {
    if (!this.platform.is(Platforms.CORDOVA)) {
      return;
    }
    this.platform.resume
      .pipe(throttleTime(2000), takeUntil(this.destroyed$))
      .subscribe(() => this.refreshActiveRestaurants());
  }

  private refreshActiveRestaurants(): void {
    if (this.restaurantStore.group?.useFlavorShifts) {
      this.orderWithFlavorStore.clearLoadedRanges();
    }
    this.restaurantStore.refreshCurrentRestaurants();
  }

  ngAfterViewInit() {
    this.swipeGestureService.init(this.routerOutlet);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private setDynamicFavicon(): void {
    if (environment.appName !== 'Kyoo') {
      this.updateFavicon('./favicon.ico', 'image/x-icon');
    }
  }

  private updateFavicon(href: string, type: string): void {
    const link: HTMLLinkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = type;
    link.rel = 'icon';
    link.href = href;
    
    if (!document.querySelector("link[rel*='icon']")) {
      document.head.appendChild(link);
    }
  }

  private async updateCustomerPlatform(): Promise<void> {
    const data = await this.storageService.get(STORAGE_CUSTOMER_DATA);
    if (!data || !data.phone) {
      return;
    }
    if (this.platform.is(Platforms.CORDOVA)) {
      this.paymentService
        .getCustomerByPhone(data.phone, false, false)
        .pipe(filter(Boolean))
        .subscribe((customer: Customer) => {
          this.paymentService.updateCustomerPlatform(customer.squareCustomerId).subscribe();
        });
    }
  }

  private loadStartCordovaPage(merchantId: string): void {
    const routeUrls = [BASE_URL_PATH];
    if (merchantId) {
      routeUrls.push(merchantId);
    }
    this.router.navigate([BASE_URL_PATH, merchantConfig.id, 'home-screen']);
  }

  private async setMerchantIfNotExists(): Promise<void> {
    const merchantId = await this.storageService.get('merchantId');
    if (merchantId) {
      this.loadStartCordovaPage(merchantId);
    } else {
      this.storageService.set('merchantId', merchantConfig.id);
      this.loadStartCordovaPage(merchantConfig.id);
    }
  }

  /**
   * Subscribe to deep link events globally (works from any page)
   */
  private subscribeToDeepLinkEvents(): void {
    console.log('App component subscribing to deep link events');

    this.deepLinkService.getDeepLinkEvents()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        console.log('Deep link event received in app.component:', event);
        if (event.type === 'giftcard') {
          // Handle gift card deep link immediately if customer is logged in
          const customerId = this.paymentStore.customer?.squareCustomerId;
          if (customerId) {
            console.log('Customer is logged in, processing gift card immediately');
            this.handleGiftCardDeepLink(customerId, event.data.merchantId, event.data.gan);
          } else {
            console.log('Customer not logged in yet, gift card stored in localStorage for later');
            // Gift card is already stored in localStorage by handleDeepLink() method
            // It will be processed when user signs in via home-screen.component.ts
          }
        }
      });
  }

  /**
   * Handle gift card deep link (can be called from any page)
   */
  private handleGiftCardDeepLink(customerId: string, merchantId: string, gan: string): void {
    console.log('handleGiftCardDeepLink called with:', { customerId, merchantId, gan });

    this.paymentService.addGiftCardToCustomer(customerId, merchantId, gan).pipe(
      switchMap((response) => {
        // Clear the pending gift card from storage
        this.storageService.remove('pendingGiftCard');
        // Show success message
        if (response) {
          this.presentAlert('Success', 'Gift card successfully added to your account!');
          // Refresh customer data to show the new gift card
          return from(this.storageService.get(STORAGE_CUSTOMER_DATA)).pipe(
            switchMap((customerStorageData) => {
              if (!customerStorageData?.token || !customerStorageData?.phone) {
                return of(null);
              }
              return this.paymentService.getVerifiedCustomerByPhone(
                customerStorageData.phone,
                customerStorageData.token,
                false, // isSharedCardsEnabled - default to false
                false  // isNeedSharingPhoneNumber - default to false
              );
            }),
            tap((customer) => {
              if (customer) {
                this.paymentStore.customer = customer;
                console.log('Customer data refreshed with new gift card');
              }
            }),
            catchError((error) => {
              console.error('Error refreshing customer data:', error);
              return of(null);
            })
          );
        }
        return of(null);
      }),
      catchError((error) => {
        // Clear the pending gift card from storage to prevent retry loops
        this.storageService.remove('pendingGiftCard');

        // Check if it's a duplicate/already linked error
        const errorMessage = error?.error?.message || error?.message || '';
        console.error('Error adding gift card:', errorMessage);

        if (errorMessage.toLowerCase().includes('already') || errorMessage.toLowerCase().includes('linked')) {
          this.presentAlert('Already Added', 'This gift card is already in your account!');
        } else {
          this.presentAlert('Error', 'Failed to add gift card. Please try again later.');
        }
        return of(null);
      })
    ).subscribe();
  }

  /**
   * Present an alert to the user
   */
  private async presentAlert(header: string, message: string): Promise<void> {
    const alert = await this.alertController.create({
      header,
      message,
      buttons: ['OK']
    });
    await alert.present();
  }
}
